import { useCallback, useEffect, useRef, useState } from "react";

import { useApolloClient, isApolloError, ApolloError } from "@apollo/client";
import { useAuth0 } from "@auth0/auth0-react";
import { Flex, IconButton } from "@chakra-ui/react";
import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import { useIntl } from "react-intl";
import { useShallow } from "zustand/react/shallow";

import { PageName } from "analytics/events";
import { processNextOrderForPickingResponse } from "app/hooks/useFetchNextOrder";
import {
  GetOrderForPickingDocument,
  GetOrderForPickingQuery,
} from "app/queries/orderSummary/orderSummary.generated";
import { useAppBackgroundEffectsStore } from "app/stores/useAppBackgroundEffectsStore";
import { useEmployeeStore } from "app/stores/useEmployeeStore";
import { openOngoingActivityModal } from "app/stores/useLayoutStore";
import { ExternalProviderOrderNotice } from "flows/Picking/components/ExternalProviderOrderNotice";
import { NewOrderDetails } from "flows/Picking/components/NewOrderDetails/NewOrderDetails";
import {
  PickingConflictData,
  PickingConflictModal,
} from "flows/Picking/components/PickingConflictModal";
import { StartPickingRefusalModal } from "flows/Picking/components/StartPickingRefusalModal";
import { useOnlineStatus } from "flows/Picking/hooks/useOnlineStatus";
import {
  serializeOrderDetails,
  serializeStartPickingOrderResponse,
} from "flows/Picking/models/order/serializer";
import { OrderDetails } from "flows/Picking/models/order/types";
import {
  GetOrderDetailsDocument,
  GetOrderDetailsQuery,
  GetOrderDetailsQueryVariables,
  useStartPickingOrderMutation,
} from "flows/Picking/queries/order/order.generated";
import { getExternalProvider } from "flows/Picking/utils/externalProviderOrder";
import { extractConflictData, isPickingConflictError } from "flows/Picking/utils/pickingConflict";
import {
  getStartPickingRefusalCode,
  StartPickingRefusalCode,
} from "flows/Picking/utils/startPickingRefusal";
import { Page } from "shared/components/Page";
import { useAnalytics } from "shared/hooks/useAnalytics";
import { useCustomToast } from "shared/hooks/useCustomToast";
import { useErrorToastWithAudio } from "shared/hooks/useErrorToastWithAudio";
import { useHubSlug } from "shared/hooks/useHubSlug";
import { IntlMessageId } from "shared/lang";
import { useTimedActivityStore } from "shared/stores/useTimedActivityStore";
import { ScanIcon, SignIcon } from "ui/Icons/Icons";
import { PagePlaceholder } from "ui/PagePlaceholder/PagePlaceholder";
import { isNotNullNorUndefined } from "utils/tsHelpers";

import { usePickingActions } from "../../hooks/usePickingActions";
import { usePickingStore } from "../../stores/usePickingStore";

export function PickingIndexPage() {
  const intl = useIntl();
  const { showToastUI } = useCustomToast();
  const { showToastAndPlayError } = useErrorToastWithAudio({
    errorToastId: "start-picking-error",
  });
  const isOnline = useOnlineStatus();
  const hubSlug = useHubSlug();
  const { isAuthenticated, user } = useAuth0();
  const { isIdentified, badgeNo } = useEmployeeStore(
    useShallow((state) => ({
      isIdentified: state.isIdentified,
      badgeNo: state.badgeNo,
    })),
  );
  const { goToStuck, goToManualPicking, startPicking } = usePickingActions();
  const { sendSegmentTrackEvent } = useAnalytics();
  const client = useApolloClient();
  const [nextOrderForPicking, setNextOrderForPicking] = useState<OrderDetails | null>(null);
  const [externalProviderOrder, setExternalProviderOrder] = useState<{
    externalProvider: string;
    orderNumber: string;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [startPickingOrderMutation, { loading: isStartLoading }] = useStartPickingOrderMutation();
  const [conflictData, setConflictData] = useState<PickingConflictData | null>(null);
  const conflictOrderIdRef = useRef<string | null>(null);
  const [startPickingRefusal, setStartPickingRefusal] = useState<{
    code: StartPickingRefusalCode;
    orderId: string;
  } | null>(null);

  const timedActivityInstance = useTimedActivityStore((state) => state.timedActivityInstance);

  const nextOrderId = useAppBackgroundEffectsStore(
    (state) => state.summaryOfNextOrderForPicking?.id ?? null,
  );
  const nextOrderNumber = useAppBackgroundEffectsStore(
    (state) => state.summaryOfNextOrderForPicking?.number ?? null,
  );

  // OAs sometimes press the back button, and orders are stuck in the picking state.
  // So, we are adding this check to redirect users to the stuck page if they are not in the correct state.
  // This only needs to run on mount — not on every re-render (e.g., feature flag changes).
  useEffect(() => {
    const { endPickingPhase, pickingPhase } = usePickingStore.getState();
    if (pickingPhase !== "startManualPicking") {
      if (endPickingPhase !== "idle" || pickingPhase !== "idle") {
        goToStuck();
      }
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOrder = useCallback(
    async (orderNumber: string) => {
      try {
        setIsLoading(true);
        const { data } = await client.query<GetOrderDetailsQuery, GetOrderDetailsQueryVariables>({
          query: GetOrderDetailsDocument,
          variables: { orderNumber },
          fetchPolicy: "network-only",
        });
        const orderDetailsData = data?.getOrderDetails;
        setNextOrderForPicking(
          orderDetailsData?.id ? serializeOrderDetails(orderDetailsData) : null,
        );
        setIsLoading(false);
      } catch (error) {
        const externalProvider = getExternalProvider(error);
        if (externalProvider !== null) {
          // Polling may have moved on while this request ran: to a newer order, whose own fetch
          // owns the screen, or to no order at all. Either way this answer is stale.
          const current = useAppBackgroundEffectsStore.getState().summaryOfNextOrderForPicking;
          if (current?.number !== orderNumber) {
            if (!current?.number) setIsLoading(false);
            return;
          }
          setIsLoading(false);
          setExternalProviderOrder({ externalProvider, orderNumber });
          setNextOrderForPicking(null);
          sendSegmentTrackEvent("errorShown", {
            screen_name: PageName.NEW_ORDER_FOR_PICKING_PAGE,
            component_value: "PICKING_ORDER_EXTERNAL_PROVIDER",
          });
          return;
        }
        const errorCode =
          error instanceof ApolloError ? error.graphQLErrors?.[0]?.extensions?.code : undefined;
        showToastAndPlayError({
          title:
            errorCode === "PICKING_ORDER_NOT_FOUND"
              ? "components.picking.new-order-summary.order-not-found-title"
              : "components.picking.new-order-summary.start-picking-generic-error-title",
        });
        datadogRum.addError("Error while fetching next order", { error });
        setIsLoading(false);
      }
    },
    [client, sendSegmentTrackEvent, showToastAndPlayError],
  );

  const fetchNextOrder = useCallback(async () => {
    let orderNumber = nextOrderNumber;

    // Fallback: when polling hasn't yet populated the cache (fresh mount,
    // post-pack queue gap, polling break window), fetch the next order
    // directly so the picker doesn't wait up to one polling cycle.
    // Route through `processNextOrderForPickingResponse` so this path emits
    // `offered` and updates the polling cache exactly like polling would —
    // otherwise a fast Start-picking tap could produce a `picked` event with
    // no preceding `offered`.
    if (!orderNumber) {
      try {
        const { data } = await client.query<GetOrderForPickingQuery>({
          query: GetOrderForPickingDocument,
          fetchPolicy: "network-only",
        });

        const summary = data?.getOrderForPicking?.orderSummary;
        processNextOrderForPickingResponse(
          summary ? { id: summary.id, number: summary.number ?? null } : null,
          sendSegmentTrackEvent,
        );
        orderNumber = summary?.number || null;
      } catch (error) {
        datadogRum.addError("Error while fetching next order summary", { error });
      }
    }

    if (orderNumber) {
      await fetchOrder(orderNumber);
    } else {
      setNextOrderForPicking(null);
    }
  }, [fetchOrder, nextOrderNumber, client, sendSegmentTrackEvent]);

  useEffect(() => {
    fetchNextOrder();
  }, [nextOrderId, nextOrderNumber, fetchNextOrder]);

  const onClickManualPickingIcon = () => {
    if (isNotNullNorUndefined(timedActivityInstance)) {
      openOngoingActivityModal();
      return;
    }
    goToManualPicking();
  };

  /**
   * DEBUG ONLY: Manually refetch the next order for investigation.
   * Logs all steps to Datadog and updates state for debugging purposes.
   */
  const handleManualRefetch = useCallback(async () => {
    setIsLoading(false);

    const showErrorToast = (title?: IntlMessageId) => {
      const formattedTitle = title || "debug-error-title";
      showToastUI?.({
        id: "debug-error",
        title: formattedTitle,
      });
    };

    const showInfoToast = (title: IntlMessageId) => {
      showToastUI?.({
        id: "debug-error",
        status: "info",
        title,
      });
    };

    const fetchNextOrderSummary = async () =>
      client.query<GetOrderForPickingQuery>({
        query: GetOrderForPickingDocument,
        fetchPolicy: "network-only",
      });

    const context = {
      hubSlug,
      isAuthenticated,
      user,
      isIdentified,
      badgeNo,
      nextOrderNumber,
      connectionStatus: navigator.onLine ? "online" : "offline",
      isOnline,
    };

    try {
      datadogLogs.logger.info("picker_pool_report", context);

      datadogRum.addAction("Manual refetch triggered");

      // Fetch next order summary
      let orderNumber: string | undefined;
      let isPickerInPool = false;
      setIsLoading(true);

      try {
        const summaryResult = await fetchNextOrderSummary();
        const pickingResponse = summaryResult.data?.getOrderForPicking;
        isPickerInPool = pickingResponse?.isInPool ?? false;
        orderNumber = pickingResponse?.orderSummary?.number ?? undefined;

        datadogLogs.logger.info("Next order summary fetched", {
          ...context,
          orderNumber,
          isPickerInPool,
          requestStatus: summaryResult?.networkStatus,
        });

        if (!isPickerInPool) {
          datadogLogs.logger.info("picker_not_in_pool", {
            ...context,
            orderNumber,
            isPickerInPool,
            requestStatus: summaryResult?.networkStatus,
          });
          showInfoToast("debug-error-description.picker_not_in_pool");
          setNextOrderForPicking(null);
          setIsLoading(false);
          return;
        }

        if (!orderNumber) {
          datadogLogs.logger.info("picker_in_pool_but_no_order", {
            ...context,
            orderNumber,
            isPickerInPool,
            requestStatus: summaryResult?.networkStatus,
          });
          showInfoToast("debug-error-description.picker_in_pool_but_no_order");
          setNextOrderForPicking(null);
          setIsLoading(false);
          return;
        }
      } catch (e: unknown) {
        let title: IntlMessageId | undefined;
        if (e instanceof Error && isApolloError(e)) {
          const apolloError = e as ApolloError;
          const code = apolloError.graphQLErrors?.[0]?.extensions?.code;
          switch (code) {
            case "EMPLOYEE_OR_HUB_NOT_FOUND":
              datadogLogs.logger.error("employee_or_hub_not_found", {
                ...context,
                origin: "fetchNextOrderSummary",
                error: apolloError,
              });
              break;
            case "OPS_STATE_SERVICE_ERROR":
              datadogLogs.logger.error("error_ops_state_manager", {
                ...context,
                origin: "fetchNextOrderSummary",
                error: apolloError,
              });
              break;
            case "DISPATCHING_SERVICE_ERROR":
              datadogLogs.logger.error("picker_in_pool_but_failed_to_get_order_details", {
                ...context,
                origin: "fetchNextOrderSummary",
                error: apolloError,
              });
              title = "debug-error-description.picker_in_pool_but_failed_to_get_order_details";
              break;
            default:
              datadogLogs.logger.error("next_order_error", {
                ...context,
                origin: "fetchNextOrderSummary",
                error: apolloError,
              });
              break;
          }
          if (apolloError.networkError) {
            datadogLogs.logger.error("network_error", {
              ...context,
              origin: "fetchNextOrderSummary",
              error: apolloError,
            });
          }
        } else if (e instanceof Error) {
          datadogLogs.logger.error("request_error", {
            ...context,
            origin: "fetchNextOrderSummary",
            error: e,
          });
        } else {
          datadogLogs.logger.error("unknown_error", {
            ...context,
            origin: "fetchNextOrderSummary",
            error: e,
          });
        }
        showErrorToast(title);
        setNextOrderForPicking(null);
        setIsLoading(false);
        return;
      }

      // Fetch order details by order number
      try {
        const orderDetailsResult = await client.query<
          GetOrderDetailsQuery,
          GetOrderDetailsQueryVariables
        >({
          query: GetOrderDetailsDocument,
          variables: { orderNumber },
          fetchPolicy: "network-only",
        });
        const orderDetailsData = orderDetailsResult.data?.getOrderDetails;

        if (!orderDetailsData?.id) {
          datadogLogs.logger.warn("picker_in_pool_but_no_order_details", {
            ...context,
            order: orderDetailsData,
          });
          showInfoToast("debug-error-description.picker_in_pool_but_no_order");
          setNextOrderForPicking(null);
          setIsLoading(false);
          return;
        }

        setNextOrderForPicking(serializeOrderDetails(orderDetailsData));
        setIsLoading(false);
        datadogLogs.logger.info("Manual refetch successful", {
          context,
          orderId: orderDetailsData.id,
        });
      } catch (e) {
        let title: IntlMessageId | undefined;
        if (e instanceof Error && isApolloError(e)) {
          const apolloError = e as ApolloError;
          const { message } = apolloError;
          if (apolloError.networkError) {
            datadogLogs.logger.error("network_error", {
              ...context,
              origin: "fetchOrderDetails",
              error: apolloError,
            });
          } else if (message.includes("no next order found")) {
            datadogLogs.logger.error("picker_in_pool_but_no_order_details", {
              ...context,
              origin: "fetchOrderDetails",
              error: apolloError,
            });
            title = "debug-error-description.picker_in_pool_but_no_order";
          } else if (message.includes("dispatching-service: something went wrong")) {
            datadogLogs.logger.error("picker_in_pool_but_failed_to_get_order_details", {
              ...context,
              origin: "fetchOrderDetails",
              error: apolloError,
            });
            title = "debug-error-description.picker_in_pool_but_failed_to_get_order_details";
          } else {
            datadogLogs.logger.error("request_error", {
              ...context,
              origin: "fetchOrderDetails",
              error: apolloError,
            });
          }
        } else if (e instanceof Error) {
          datadogLogs.logger.error("request_error", {
            ...context,
            origin: "fetchOrderDetails",
            error: e,
          });
        } else {
          datadogLogs.logger.error("unknown_error", {
            ...context,
            origin: "fetchOrderDetails",
            error: e,
          });
        }
        showErrorToast(title);
        setNextOrderForPicking(null);
        setIsLoading(false);
      }
    } catch (e) {
      // Catch any unexpected errors in the main try
      datadogLogs.logger.error("unhandled_manual_refetch_error", { ...context, error: e });
      setIsLoading(false);
    }
  }, [
    badgeNo,
    client,
    hubSlug,
    isAuthenticated,
    isIdentified,
    isOnline,
    nextOrderNumber,
    showToastUI,
    user,
  ]);

  const { clearCurrentNextOrderId } = useAppBackgroundEffectsStore();

  // Returns true when the error was a start-picking refusal and the refusal modal is now showing.
  const showStartPickingRefusal = useCallback(
    (error: unknown, orderId: string) => {
      const code = getStartPickingRefusalCode(error);
      if (!code) return false;
      setStartPickingRefusal({ code, orderId });
      sendSegmentTrackEvent("errorShown", {
        screen_name: PageName.NEW_ORDER_FOR_PICKING_PAGE,
        component_value: code,
      });
      return true;
    },
    [sendSegmentTrackEvent],
  );

  // Drops the partner order so the next one loads, unless polling has already moved on from it.
  const handleExternalProviderOrderAcknowledge = useCallback(() => {
    const summary = useAppBackgroundEffectsStore.getState().summaryOfNextOrderForPicking;
    if (summary && summary.number === externalProviderOrder?.orderNumber) {
      clearCurrentNextOrderId(summary.id);
    }
    setExternalProviderOrder(null);
  }, [clearCurrentNextOrderId, externalProviderOrder]);

  const onStartPickingFromDetails = useCallback(async () => {
    const order = nextOrderForPicking;
    if (isStartLoading) return;
    if (!order?.number) {
      showToastAndPlayError({
        title: "components.picking.new-order-summary.start-picking-generic-error-title",
      });
      if (order?.id) clearCurrentNextOrderId(order.id);
      setNextOrderForPicking(null);
      return;
    }

    if (isNotNullNorUndefined(timedActivityInstance)) {
      openOngoingActivityModal();
      return;
    }

    try {
      const { data } = await startPickingOrderMutation({
        variables: { orderId: order.id, isAlwaysInbound: true, manualPicking: false },
      });
      const orderDetails = data?.startPickingOrder?.orderDetails;
      if (!orderDetails) {
        showToastAndPlayError({
          title: "components.picking.new-order-summary.start-picking-generic-error-title",
        });
        clearCurrentNextOrderId(order.id);
        return;
      }

      const serializedOrder = serializeStartPickingOrderResponse(orderDetails);
      startPicking(serializedOrder, "PICKING_SCREEN");
      clearCurrentNextOrderId(order.id);
      setNextOrderForPicking(null);
    } catch (error) {
      if (isPickingConflictError(error)) {
        const conflict = extractConflictData(error as ApolloError);
        if (conflict) {
          conflictOrderIdRef.current = order.id;
          setConflictData(conflict);
          return;
        }
      }
      if (showStartPickingRefusal(error, order.id)) return;
      showToastAndPlayError({
        title: "components.picking.new-order-summary.start-picking-generic-error-title",
      });
      datadogRum.addError("Error while starting picking from order details", { error });
      clearCurrentNextOrderId(order.id);
      setNextOrderForPicking(null);
    }
  }, [
    clearCurrentNextOrderId,
    isStartLoading,
    nextOrderForPicking,
    showStartPickingRefusal,
    showToastAndPlayError,
    startPicking,
    startPickingOrderMutation,
    timedActivityInstance,
  ]);

  const onConfirmCancelledFromDetails = useCallback(() => {
    if (nextOrderForPicking) {
      clearCurrentNextOrderId(nextOrderForPicking.id);
    }
    setNextOrderForPicking(null);
  }, [nextOrderForPicking, clearCurrentNextOrderId]);

  const handleConflictClose = useCallback(() => {
    setConflictData(null);
    conflictOrderIdRef.current = null;
  }, []);

  const handleConflictProceed = useCallback(async () => {
    setConflictData(null);
    const orderId = conflictOrderIdRef.current;
    conflictOrderIdRef.current = null;
    if (!orderId) return;

    try {
      const { data } = await startPickingOrderMutation({
        variables: { orderId, isAlwaysInbound: true, manualPicking: true },
      });
      const orderDetails = data?.startPickingOrder?.orderDetails;
      if (!orderDetails) {
        showToastAndPlayError({
          title: "components.picking.new-order-summary.start-picking-generic-error-title",
        });
        clearCurrentNextOrderId(orderId);
        setNextOrderForPicking(null);
        return;
      }

      const serializedOrder = serializeStartPickingOrderResponse(orderDetails);
      startPicking(serializedOrder, "PICKING_SCREEN");
      clearCurrentNextOrderId(orderId);
      setNextOrderForPicking(null);
    } catch (error) {
      if (showStartPickingRefusal(error, orderId)) return;
      showToastAndPlayError({
        title: "components.picking.new-order-summary.start-picking-generic-error-title",
      });
      datadogRum.addError("Error while starting picking after conflict override", { error });
      clearCurrentNextOrderId(orderId);
      setNextOrderForPicking(null);
    }
  }, [
    clearCurrentNextOrderId,
    showStartPickingRefusal,
    showToastAndPlayError,
    startPicking,
    startPickingOrderMutation,
  ]);

  // Drops the refused order so the next one loads. Polling may already have replaced the order
  // behind the modal, so only clear it when it's still the refused one.
  const handleStartPickingRefusalClose = useCallback(() => {
    if (!startPickingRefusal) return;
    const { orderId } = startPickingRefusal;
    setStartPickingRefusal(null);
    clearCurrentNextOrderId(orderId);
    setNextOrderForPicking((current) => (current?.id === orderId ? null : current));
  }, [clearCurrentNextOrderId, startPickingRefusal]);

  const renderOrderContent = () => {
    if (externalProviderOrder) {
      return (
        <ExternalProviderOrderNotice
          externalProvider={externalProviderOrder.externalProvider}
          orderNumber={externalProviderOrder.orderNumber}
          onAcknowledge={handleExternalProviderOrderAcknowledge}
        />
      );
    }

    if (!isNotNullNorUndefined(nextOrderForPicking)) {
      return (
        <PagePlaceholder
          icon={<SignIcon />}
          title={intl.formatMessage({ id: "pages.picking.picking-index.placeholder-title" })}
          subtitle={intl.formatMessage({ id: "pages.picking.picking-index.placeholder-subtitle" })}
          ctaLabel={intl.formatMessage({ id: "pages.picking.picking-index.refresh-button-label" })}
          onClickCta={handleManualRefetch}
          ctaVariant="link"
        />
      );
    }

    return (
      <NewOrderDetails
        orderDetails={nextOrderForPicking}
        onStartPicking={onStartPickingFromDetails}
        onConfirmCancelled={onConfirmCancelledFromDetails}
        isStartPickingLoading={isStartLoading}
      />
    );
  };

  return (
    <Page isCentered={!nextOrderForPicking?.id} isLoading={isLoading} isBgGrey>
      <Flex w="100%" justifyContent="flex-end">
        <IconButton
          bg="pinkFlink.200"
          height="40px"
          width="40px"
          aria-label="scan-manually"
          icon={<ScanIcon color="pinkFlink.500" height="22px" width="22px" />}
          onClick={onClickManualPickingIcon}
        />
      </Flex>
      {renderOrderContent()}
      {conflictData && (
        <PickingConflictModal
          isOpen
          conflictData={conflictData}
          onClose={handleConflictClose}
          onProceed={handleConflictProceed}
        />
      )}
      {startPickingRefusal && (
        <StartPickingRefusalModal
          isOpen
          refusalCode={startPickingRefusal.code}
          onClose={handleStartPickingRefusalClose}
        />
      )}
    </Page>
  );
}
